import { useState, useEffect } from "react";
import { Table, ScrollArea, Text, LoadingOverlay, Alert } from "@mantine/core";
import SearchInput from "@app/components/DashAdmin/SearchInput";
import { sortData, Th } from "@app/components/DashAdmin/DashAdmin.helpers";
import { ApolloError, gql, useQuery } from "@apollo/client";
import { IconAlertCircle } from "@tabler/icons";
import { JURY_ROLE_ID } from "src/consts";

export interface JuryUserTableItem {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  role: {
    id: string;
    name: string;
  };
}

export const JURY_QUERY = gql`
  query Juries($filters: UserFilters) {
    users(filters: $filters) {
      totalCount
      nodes {
        ... on User {
          firstName
          lastName
          email
          phoneNumber
          role {
            name
            id
          }
        }
      }
    }
  }
`;

export default function JuryTableWrapper() {
  const { loading, error, data } = useQuery(JURY_QUERY, {
    variables: {
      filters: {
        roleId: [JURY_ROLE_ID],
        isActive: true,
      },
    },
  });
  const users = data?.users?.nodes || [];

  return <JuryTable {...{ loading, error, users }} />;
}

interface JuryTableProps {
  loading: boolean;
  error: ApolloError;
  users: JuryUserTableItem[];
}

export function JuryTable(props: JuryTableProps) {
  const { loading, error, users } = props;

  const [search, setSearch] = useState("");
  const [sortedData, setSortedData] = useState([]);
  const [sortBy, setSortBy] = useState<keyof JuryUserTableItem | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);

  useEffect(() => {
    setSortedData(users);
  }, [users]);

  const setSorting = (field: keyof JuryUserTableItem) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    setSortedData(sortData(users, { sortBy: field, reversed, search }));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearch(value);
    const _tmp = sortData(users, {
      sortBy,
      reversed: reverseSortDirection,
      search: value,
    });
    setSortedData(_tmp);
  };

  const rows = sortedData.map((row) => {
    return (
      <tr key={row.id}>
        <td>{row.firstName} </td>
        <td>{row.lastName}</td>
        <td>{row.phoneNumber}</td>
        <td>{row.email}</td>
      </tr>
    );
  });

  return (
    <div className="container my-10">
      <div className="flex flex-col md:flex-row gap-4 justify-between">
        <h1 className="text-2xl font-semibold">Liste des jurys</h1>
        <SearchInput search={search} handleSearchChange={handleSearchChange} />
      </div>
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} title="Oups !" color="red">
          Une erreur s&apos;est produite lors de l&apos;extraction des données
          nécessaires. L&apos;équipe de support est prévenue, veuillez réessayer
          plus tard.
        </Alert>
      )}
      <ScrollArea>
        <LoadingOverlay visible={loading} overlayBlur={2} />
        <Table
          horizontalSpacing="md"
          verticalSpacing="xs"
          sx={{ tableLayout: "fixed", minWidth: 700 }}
          className="!border !border-grey-light"
          highlightOnHover
        >
          <thead>
            <tr>
              <Th
                sorted={sortBy === "firstName"}
                reversed={reverseSortDirection}
                onSort={() => setSorting("firstName")}
              >
                Prénom
              </Th>
              <Th
                sorted={sortBy === "lastName"}
                reversed={reverseSortDirection}
                onSort={() => setSorting("lastName")}
              >
                Nom de famille
              </Th>
              <Th
                sorted={sortBy === "phoneNumber"}
                reversed={reverseSortDirection}
                onSort={() => setSorting("phoneNumber")}
              >
                Téléphone
              </Th>
              <Th
                sorted={sortBy === "email"}
                reversed={reverseSortDirection}
                onSort={() => setSorting("email")}
              >
                Courriel
              </Th>
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows
            ) : (
              <tr>
                <td colSpan={5}>
                  <Text weight={500} align="center">
                    Aucune information disponible.
                  </Text>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </ScrollArea>
    </div>
  );
}
